import { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import xeniaApi from 'api/index';
import CustomButton from 'components/Button/CustomButton';
import CustomToolTip from 'components/Tooltip';
import CheckIcon from '@mui/icons-material/CheckCircle';
import VideoCameraIcon from 'components/Icons/videoCameraIcon';
import DIALOGS from 'utils/dialogIds';
import actions from 'store/actions';
import { useDispatch } from 'react-redux';

const VideoUploaderSection = ({ announcement, video, setVideo, readOnly }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const handleVideoUpload = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];

    // Check file size (30MB max)
    if (file.size > 30 * 1024 * 1024) {
      console.log('Video file must be less than 30MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('video/')) {
      console.log('Please select a valid video file');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Upload video to server
      const formData = new FormData();
      formData.append('file', file);

      const response = await xeniaApi.CreateTaskAttchmentFilesApi([file]);

      // Process response to get file URL
      if (response.filePath && response.filePath.length > 0) {
        setVideo({
          id: `video-${Date.now()}`,
          name: file.name,
          type: file.type,
          size: file.size,
          fileUrl: response.baseUrl + response.filePath[0],
          file: file,
        });
      } else {
        throw new Error('Failed to upload video');
      }
    } catch (err) {
      console.error('Error uploading video:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveVideo = () => {
    setVideo(null);
  };

  const handlePreviewVideo = () => {
    dispatch(
      actions.setDialog({
        dialogId: DIALOGS.PHOTOS_ATTACHMENTS_PREVIEW,
        open: true,
        data: {
          allAttachments: [
            { url: video?.fileUrl || video, name: 'Video', type: 'video' },
          ],
          id: video?.fileUrl || video,
          removeItem: handleRemoveVideo,
        },
      }),
    );
  };

  return (
    <Box
      sx={{
        mb: 3,
        background: '#fff',
        border: '1px solid #EEEEEE',
        borderRadius: '10px',
        padding: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="subtitle1" fontWeight="700" fontSize={'14px'}>
        {video ? (
          <CheckIcon sx={{ color: '#04B88D', mr: 1 }} />
        ) : (
          <VideoCameraIcon sx={{ color: '#4E48FA', mr: 1 }} />
        )}{' '}
        Video Required
      </Typography>
      <Box display={'flex'} alignItems="center" gap={1}>
        {video ? (
          <CustomToolTip title={video.name ?? 'View Video'} placement="top">
            <Box
              sx={{
                position: 'relative',
                width: '60px',
                height: '60px',
                backgroundColor: '#f0f0f0',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                mb: 1,
                cursor: 'pointer',
                overflow: 'hidden',
              }}
              onClick={handlePreviewVideo}
            >
              <video
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              >
                <source src={video?.fileUrl || video} />
              </video>
            </Box>
          </CustomToolTip>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {!readOnly ? (
              <>
                {' '}
                <input
                  accept="video/*"
                  id="video-upload-input"
                  type="file"
                  onChange={handleVideoUpload}
                  disabled={announcement.status !== 'active' || uploading}
                  style={{
                    opacity: 0,
                    position: 'absolute',
                    zIndex: 3,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                  }}
                />
                <label htmlFor="video-upload-input">
                  <CustomButton
                    variant="contained"
                    buttonLoader={uploading}
                    disabled={uploading || announcement.status !== 'active'}
                    sx={{
                      borderRadius: '6px',
                      padding: '5px 12px',
                      fontSize: 12,
                      textTransform: 'none',
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Record Video'}
                  </CustomButton>
                </label>
              </>
            ) : null}

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mt: 1 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VideoUploaderSection;
